import { z } from 'zod'
import { PackageRulesSchema, ShoppingPromotionSchema } from '@reward-platform/shared-schemas'
import {
  AddressSchema,
  AgeGroupSchema,
  CarHireItemType,
  CarHireLocationSchema,
  DateSchema,
  ISODateStringSchema,
  ExperienceItemType,
  HotelItemType,
  MonetaryAmountSchema,
  RoomSchema,
  TravelerGroupSchema,
  VehicleSchema,
  FlightItemType,
  SurchargesMonetaryAmountSchema,
  TaxDetailsSchema,
  CarHirePolicySchema,
  ExperienceCancellationPolicySchema,
  ExperienceDurationSchema,
  ExperienceLocationSchema,
  BalanceBoosterSchema,
  FlightJourneySchema,
} from '../common'

const BasketItemTypeSchema = z.union([
  z.literal(HotelItemType),
  z.literal(ExperienceItemType),
  z.literal(CarHireItemType),
  z.literal(FlightItemType),
])

export type BasketItemType = z.infer<typeof BasketItemTypeSchema>

const PaymentOptionSchema = z.object({
  id: z.string(),
  cashValue: MonetaryAmountSchema.optional(),
  aviosValue: z.number(),
  cashBaseValue: MonetaryAmountSchema.optional(),
  cashTaxValue: MonetaryAmountSchema.optional(),
  taxDetails: z.array(TaxDetailsSchema).optional(),
  grandTotal: MonetaryAmountSchema.optional(),
  initialLoyaltyAmount: z.number().optional(),
  promotions: z.array(ShoppingPromotionSchema).optional(),
  aviosCollectValue: z.number().optional(),
})
export type PaymentOption = z.infer<typeof PaymentOptionSchema>

/**
 * There are 4 cash values that come from the API for the cash only option
 * `baseAmount`, `totalAmount` and `totalTaxAmount`. There are mapped in the following way:
 * `totalAmount` -> `cashTotalValue`
 * `totalTaxAmount` -> `cashTaxValue`
 * `baseAmount` -> `cashBaseValue`
 * `taxDetails   -> `taxDetails`
 *
 */
const OnlyCashPaymentOptionSchema = z.object({
  id: z.string(),
  cashTotalValue: MonetaryAmountSchema,
  cashBaseValue: MonetaryAmountSchema,
  cashTaxValue: MonetaryAmountSchema.optional(),
  grandTotal: MonetaryAmountSchema.optional(),
  balanceBoosters: z.array(BalanceBoosterSchema).optional(),
  aviosCollectValue: z.number(),
  baseLoyalty: z.number().optional(),
  salesTax: MonetaryAmountSchema.optional(),
  taxDetails: z.array(TaxDetailsSchema).optional(),
  promotions: z.array(ShoppingPromotionSchema).optional(),
})
export type OnlyCashPaymentOption = z.infer<typeof OnlyCashPaymentOptionSchema>

export const PaymentOptionsSchema = z.object({
  onlyCashPaymentOption: OnlyCashPaymentOptionSchema.nullish(),
  options: z.array(PaymentOptionSchema),
  defaultSelectedId: z.string().optional(),
  isSalesTaxType: z.boolean(),
})
export type PaymentOptions = z.infer<typeof PaymentOptionsSchema>

interface FormattedSurchargesBreakdown {
  feeName?: string
  amount: string
}

interface FormattedSurchargesSchema {
  total: string | undefined
  breakdown?: FormattedSurchargesBreakdown[]
}

export type FormattedSurcharges = FormattedSurchargesSchema

const BaseBasketItemSchema = z.object({
  id: z.string(),
  bundleSelectionId: z.string(),
  type: BasketItemTypeSchema,
  startDate: ISODateStringSchema,
  imageUrl: z.string().optional(),
  paymentOptions: PaymentOptionsSchema,
  surcharges: z.array(SurchargesMonetaryAmountSchema),
})
export type BaseBasketItem = z.infer<typeof BaseBasketItemSchema>

const TravelerSchema = z.object({
  id: z.string(),
  age: z.number().optional(),
  ageGroup: AgeGroupSchema,
})

export type Traveler = z.infer<typeof TravelerSchema>

const FlightTravelerSchema = TravelerSchema
export type FlightTraveler = z.infer<typeof FlightTravelerSchema>

const HotelTravelerSchema = TravelerSchema

export type HotelTraveler = z.infer<typeof HotelTravelerSchema>

const HotelTravelersSchema = z.array(z.array(HotelTravelerSchema))
export type HotelTravelers = z.infer<typeof HotelTravelersSchema>

export const HotelBasketItemSchema = BaseBasketItemSchema.extend({
  name: z.string(),
  type: z.literal(HotelItemType),
  room: RoomSchema,
  endDate: ISODateStringSchema,
  address: AddressSchema,
  travelers: HotelTravelersSchema,
  packageRules: PackageRulesSchema,
  vendorId: z.string().optional(),
  vendorName: z.string().optional(),
  amenities: z.array(z.string()).optional(),
})
export type HotelBasketItem = z.infer<typeof HotelBasketItemSchema>

const PerTravelerQuestionGroup = 'PER_TRAVELER'
const PerBookingQuestionGroup = 'PER_BOOKING'

const BookingQuestionGroupSchema = z.enum([PerTravelerQuestionGroup, PerBookingQuestionGroup])

export const SPECIAL_REQUIREMENTS_BOOKING_QUESTION_ID = 'SPECIAL_REQUIREMENTS' as const
export const PICKUP_POINT_BOOKING_QUESTION_ID = 'PICKUP_POINT' as const

const perBookingQuestionIds = [
  SPECIAL_REQUIREMENTS_BOOKING_QUESTION_ID,
  PICKUP_POINT_BOOKING_QUESTION_ID,
] as const

const PerBookingQuestionIdSchema = z.enum(perBookingQuestionIds)

export const DATE_OF_BIRTH_BOOKING_QUESTION_ID = 'DATE_OF_BIRTH' as const
export const HEIGHT_BOOKING_QUESTION_ID = 'HEIGHT' as const
export const PASSPORT_NUMBER_BOOKING_QUESTION_ID = 'PASSPORT_PASSPORT_NO' as const
export const PASSPORT_NATIONALITY_BOOKING_QUESTION_ID = 'PASSPORT_NATIONALITY' as const
export const PASSPORT_EXPIRY_BOOKING_QUESTION_ID = 'PASSPORT_EXPIRY' as const

export const WEIGHT_BOOKING_QUESTION_ID = 'WEIGHT' as const

const perTravelerQuestionIds = [
  'FULL_NAMES_FIRST',
  'FULL_NAMES_LAST',
  'AGEBAND',
  DATE_OF_BIRTH_BOOKING_QUESTION_ID,
  HEIGHT_BOOKING_QUESTION_ID,
  WEIGHT_BOOKING_QUESTION_ID,
  PASSPORT_NUMBER_BOOKING_QUESTION_ID,
  PASSPORT_NATIONALITY_BOOKING_QUESTION_ID,
  PASSPORT_EXPIRY_BOOKING_QUESTION_ID,
] as const

const PerTravelerQuestionIdSchema = z.enum(perTravelerQuestionIds)

const BookingQuestionIdSchema = z.union([PerBookingQuestionIdSchema, PerTravelerQuestionIdSchema])

export type BookingQuestionId = z.infer<typeof BookingQuestionIdSchema>

export const LOCATION_REFERENCE_UNIT = 'LOCATION_REFERENCE' as const
export const FREETEXT_UNIT = 'FREETEXT' as const

const BookingQuestionSchema = z.object({
  id: BookingQuestionIdSchema,
  type: z.enum(['STRING', 'NUMBER_AND_UNIT', 'DATE', 'TIME', 'LOCATION_REF_OR_FREE_TEXT']),
  allowedAnswers: z.array(z.string()).optional(),
  answerIsRequired: z.enum(['MANDATORY', 'OPTIONAL', 'CONDITIONAL']),
  group: BookingQuestionGroupSchema,
  maxLength: z.number(),
  title: z.string().optional(),
  hint: z.string().optional(),
  units: z.array(z.string()).optional(),
})

export type BookingQuestion = z.infer<typeof BookingQuestionSchema>

const ExperienceLanguageGuideTypeSchema = z.enum(['GUIDE', 'AUDIO', 'WRITTEN'])

const ExperienceLanguageGuideSchema = z.object({
  type: ExperienceLanguageGuideTypeSchema,
  language: z.string(),
})

export type ExperienceLanguageGuide = z.infer<typeof ExperienceLanguageGuideSchema>

export const ExperienceBasketItemSchema = BaseBasketItemSchema.extend({
  name: z.string(),
  type: z.literal(ExperienceItemType),
  description: z.string(),
  vendorName: z.string().optional(),
  endDate: ISODateStringSchema,
  duration: ExperienceDurationSchema.optional(),
  days: z.number().optional(),
  experienceType: z.string().optional(),
  bookingQuestions: z.array(BookingQuestionSchema),
  travelers: TravelerGroupSchema.array(),
  allowCustomTravelerPickup: z.boolean(),
  startTime: z.string().optional(),
  languageGuides: z.array(ExperienceLanguageGuideSchema).optional(),
  cancellationPolicy: ExperienceCancellationPolicySchema,
  additionalInfo: z.array(z.string()).optional(),
  startLocations: ExperienceLocationSchema.array().optional(),
  endLocations: ExperienceLocationSchema.array().optional(),
  pickupLocations: ExperienceLocationSchema.array().optional(),
})
export type ExperienceBasketItem = z.infer<typeof ExperienceBasketItemSchema>

export const Avis = 'Avis'
const Budget = 'Budget'

const CarHireVendorSchema = z.union([z.literal(Avis), z.literal(Budget)])

export type CarHireVendor = z.infer<typeof CarHireVendorSchema>

const CarHireExtraSchema = z.object({
  serviceRef: z.string().optional(),
  serviceSubType: z.string().optional(),
  name: z.string().optional(),
  serviceCategory: z.string().optional(),
  description: z.string().optional(),
  selectedQuantity: z.number().optional(),
  maxAllowedQuantity: z.number().optional(),
})

const CarHireIncludedBasketSchema = z.object({
  attribute: z.string(),
  value: z.string().optional(),
})

export type CarHireIncludedBasket = z.infer<typeof CarHireIncludedBasketSchema>

export const CarHireBasketItemSchema = BaseBasketItemSchema.extend({
  type: z.literal(CarHireItemType),
  vendorName: CarHireVendorSchema,
  endDate: ISODateStringSchema,
  pickUpLocation: CarHireLocationSchema,
  dropOffLocation: CarHireLocationSchema,
  locationPolicy: CarHirePolicySchema,
  rentalPolicy: CarHirePolicySchema,
  locationRentalPolicy: CarHirePolicySchema,
  vehicle: VehicleSchema,
  extras: z.array(CarHireExtraSchema),
  included: z.array(CarHireIncludedBasketSchema).optional(),
  transmission: z.string().optional(),
  doors: z.string().optional(),
  seats: z.string().optional(),
  bags: z.string().optional(),
  under30DriverDateOfBirth: DateSchema.optional(),
  unlimitedMileage: z.boolean().optional(),
  totalMileage: z.string().optional(),
  airCondition: z.string().optional(),
})
export type CarHireBasketItem = z.infer<typeof CarHireBasketItemSchema>

export const FlightBasketItemSchema = BaseBasketItemSchema.extend({
  inboundFlightJourney: FlightJourneySchema.optional(),
  outboundFlightJourney: FlightJourneySchema,
  vendorId: z.string().optional(),
  vendorName: z.string().optional(),
  travelers: FlightTravelerSchema.array(),
  isSecure: z.boolean().optional(),
})

export type FlightBasketItem = z.infer<typeof FlightBasketItemSchema>

const BasketItemSchema = z.union([
  HotelBasketItemSchema,
  ExperienceBasketItemSchema,
  CarHireBasketItemSchema,
  FlightBasketItemSchema,
])

export type BasketItem = z.infer<typeof BasketItemSchema>

const BasketItemsSchema = z.object({
  [HotelItemType]: z.array(HotelBasketItemSchema),
  [ExperienceItemType]: z.array(ExperienceBasketItemSchema),
  [CarHireItemType]: z.array(CarHireBasketItemSchema),
  [FlightItemType]: z.array(FlightBasketItemSchema),
})

export type BasketItems = z.infer<typeof BasketItemsSchema>

export const BasketSchema = z.object({
  correlationId: z.string(),
  timestamp: z.string().transform((str) => new Date(str)),
  items: BasketItemsSchema,
  currency: z.string(),
})
export type Basket = z.infer<typeof BasketSchema>

export const isHotelBasketItem = (basketItem: BaseBasketItem): basketItem is HotelBasketItem => {
  return basketItem.type === HotelItemType
}

export const isExperienceBasketItem = (
  basketItem: BaseBasketItem
): basketItem is ExperienceBasketItem => {
  return basketItem.type === ExperienceItemType
}

export const isCarBasketItem = (basketItem: BaseBasketItem): basketItem is CarHireBasketItem => {
  return basketItem.type === CarHireItemType
}

export const isFlightBasketItem = (basketItem: BaseBasketItem): basketItem is FlightBasketItem => {
  return basketItem.type === FlightItemType
}

const BasketOfferBaseSchema = z.object({
  bundleId: z.string(),
  recommendationId: z.string(),
  correlationId: z.string(),
  priceApplicabilityId: z.string(),
})

export const CarHireBasketOfferSchema = BasketOfferBaseSchema
export type CarHireBasketOffer = z.infer<typeof CarHireBasketOfferSchema>

export const HotelBasketOfferSchema = BasketOfferBaseSchema.extend({
  bedGroupId: z.string(),
})
export type HotelBasketOffer = z.infer<typeof HotelBasketOfferSchema>

export const ExperienceBasketOfferSchema = BasketOfferBaseSchema.extend({
  experienceStartTime: z.string().optional(),
})
export type ExperienceBasketOffer = z.infer<typeof ExperienceBasketOfferSchema>

export const FlightBasketOfferSchema = BasketOfferBaseSchema.extend({
  vendorCode: z.enum(['EI']), // based on CarrierSchema in flight schema
  shopperGroup: z.object({
    shopperRef: z.array(z.string()),
  }),
})

export type FlightBasketOffer = z.infer<typeof FlightBasketOfferSchema>
